import { canTransition } from './state/transition.js';
/**
 * The recall engine's record keeping. §6.14 `APT-004` to `APT-006`; Appendix Z's recall response
 * report.
 *
 * `appointments.ts` holds the arithmetic: {@link recallDue} works out the day a customer falls due,
 * `recallSendDate` moves it off a closed day, `decideRecall` reads consent and suppression, and
 * `replenishmentNudgeDates` repeats a contact lens nudge. This file holds what a stored recall
 * **is**: which four kinds exist, what states one moves through, and what a campaign may ask for.
 * The two are separate because the arithmetic is pure of any notion of a row.
 *
 * ---
 *
 * **Neither set below is in Appendix E or Appendix C, and both come from the spec anyway.**
 *
 * The four kinds are `APT-004`'s own list: "sight-test recall at N months from last test; contact
 * lens re-order recall based on modality and quantity sold ...; frame refresh recall;
 * warranty-expiry courtesy contact." `RecallRule` in `appointments.ts` already names them, and this
 * exports the list so a database column can be checked against it.
 *
 * The states are Appendix Z's recall response report: "campaign, sent, delivered, responded,
 * converted, revenue". Those are the stages the shop is told to measure, so they are the stages a
 * row moves through. **`delivered` is deliberately not a state.** `MSG-005` requires delivery status
 * tracking and there is no messaging channel to track, so a state nothing could ever set would make
 * the report's `delivered` column look implemented when it is not.
 *
 * Both absences are recorded as spec gaps rather than filled by inventing an appendix entry. The
 * precedent is `TENANT_STATUSES` and `PRESCRIPTION_STATUSES`, which `packages/db` checks with
 * `valuesCheck` against a domain constant for the same reason.
 */
/** `APT-004`'s four rules, in the order the requirement lists them. */
export const RECALL_KINDS = [
    'sight_test',
    'contact_lens',
    'frame_refresh',
    'warranty_expiry',
];
export const RECALL_STATUSES = [
    /** Computed by a campaign run and not yet acted on. */
    'due',
    /**
     * `APT-005`: consent or suppression said no. A terminal state that is **recorded**, not a row
     * that is skipped, because "we did not contact this customer and here is why" is the answer to
     * the only question anybody asks about a recall that never arrived.
     */
    'withheld',
    /** The shop contacted the customer. In v1 that is an email, an in-app item, or by hand. */
    'sent',
    /** The customer answered. Appendix Z counts these against `sent`. */
    'responded',
    /** The customer bought, and `converted_order_id` says what. Appendix Z's revenue column. */
    'converted',
    /**
     * The due date passed far enough that contacting the customer about it is no longer the same
     * conversation. Keeps an old row out of today's list without deleting it (nothing is deleted).
     */
    'expired',
];
/**
 * Total, as `state/transition.ts` asks.
 *
 * `due -> withheld` and `due -> sent` are the two answers a campaign run can give. A `withheld` row
 * can still reach `sent`, because `APT-005` evaluates consent "as it stands when the send runs" and
 * a customer who consents next week is contactable next week; the row is not re-created, it moves.
 *
 * `sent -> expired` exists and `responded -> expired` does not: a customer who answered has had the
 * conversation, and letting that lapse into `expired` would lose the answer. `converted` is
 * terminal, and so is `expired`.
 */
export const RECALL_TRANSITIONS = {
    due: ['withheld', 'sent', 'expired'],
    withheld: ['sent', 'expired'],
    sent: ['responded', 'converted', 'expired'],
    // A customer can buy without a separate "responded" step, and often does: they walk in.
    responded: ['converted'],
    converted: [],
    expired: [],
};
export function canRecallTransition(from, to) {
    return canTransition(RECALL_TRANSITIONS, from, to);
}
/** Why a recall was not sent. `decideRecall`'s two refusals, stored. */
export const RECALL_WITHHELD_REASONS = ['suppressed', 'no_consented_channel'];
/**
 * The channels a recall may actually go out on **in this release**.
 *
 * `MSG-001`: "Channels: email and in-app in v1. WhatsApp and SMS at v3." `MarketingChannel` in
 * `appointments.ts` is the wider set `decideRecall` reasons about, because a customer's consent
 * rows and their preference exist for channels this release cannot use, and dropping them would
 * lose a consent the shop recorded. This is the narrower set a send may name.
 *
 * `MSG-001a` records what that costs in Pakistan: `PK-021` makes WhatsApp the primary customer
 * channel and shop customers largely do not use email, so "the mitigation in v1 is printed and
 * downloadable artifacts the shop sends themselves". `by_hand` is that mitigation, named rather
 * than left as an email nobody reads: the shop phoned them, and the row says so.
 */
export const RECALL_SEND_CHANNELS = ['email', 'in_app', 'by_hand'];
/**
 * What a rule needs before it can be evaluated, by kind.
 *
 * Every interval in `APT-004` and `APT-006` is left blank by the spec: N months for a sight test,
 * the days a unit of each modality lasts, the reorder lead, the nudge cadence and the cap. Per
 * `decisions/0004` none of them is defaulted anywhere, so a rule is **inactive until a shop sets
 * its own**, and this is the list a stored rule is checked against. The database holds the same
 * shape as a constraint, so an unusable rule cannot be marked active.
 */
export const RECALL_RULE_FIELDS = {
    sight_test: ['intervalMonths'],
    contact_lens: ['daysPerUnit', 'reorderLeadDays'],
    frame_refresh: ['intervalMonths'],
    warranty_expiry: ['leadDays'],
};
/**
 * Whether a recall of this kind can repeat. `APT-006` makes replenishment nudges a repeating
 * sequence and names no cadence; nothing in `APT-004` repeats a sight test or a warranty courtesy
 * contact, so a second nudge for those would be the software deciding to chase a customer twice.
 */
export const RECALL_KINDS_THAT_NUDGE = ['contact_lens'];
//# sourceMappingURL=recall.js.map